import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';
import {addMonths} from 'date-fns/addMonths';
import {startOfMonth} from 'date-fns/startOfMonth';
import pick from 'lodash/pick';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalContent,
  ModalHeader,
  ModalSubmitButton,
  NewTextInputField,
  showModal,
} from '@edna/components';
import {useOnChange} from '@edna/hooks';

import {ModalCenterImage, Text} from 'src/components/primitives';
import {EChannelType} from 'src/constants';
import {DEFAULT_ACTIVATION_TIME, ELicenseLabelType} from 'src/containers/TariffsCatalog';
import {CalendarBoardImage, PlanSuccessImage} from 'src/images';
import {combineDateAndTime, joiResolver} from 'src/utils';

import {
  useChangeAvailableTariffMutation,
  useGetCatalogTariffLicenseQuery,
  useGetConnectedTariffsQuery,
} from '../api';
import {DEFAULT_COEFFICIENT, TAvailableTariff} from '../definitions';
import {getLinkedSubjects} from '../utils';
import {CoefficientsForm} from './CoefficientsForm';
import {showLinkedSubjectsModal} from './LinkedSubjectsModal';
import * as S from './style';

const ConfirmModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TAvailableTariff;
}>(({closeModal, tariff}) => {
  const {t} = useTranslation();
  const {id: tenantId} = useParams();

  const {data: connectedTariffs, fulfilledTimeStamp: connectedTariffsTimeStamp} =
    useGetConnectedTariffsQuery(tenantId ?? skipToken);

  const linkedSubjects = React.useMemo(
    () => getLinkedSubjects(connectedTariffs),
    [connectedTariffsTimeStamp],
  );

  const subjects = linkedSubjects[tariff.id];

  const [changeTariff, {isLoading}] = useChangeAvailableTariffMutation();

  const handleSubmit = React.useCallback(() => {
    changeTariff(tariff)
      .unwrap()
      .then(closeModal)
      .catch(() => {});
  }, [tariff, closeModal]);

  return (
    <Modal width="430px">
      <ModalHeader>{t('Tariffs:confirmEditModal.title')}</ModalHeader>
      <ModalContent>
        <Text secondary>
          <Trans i18nKey={`Tariffs:confirmEditModal.${subjects ? 'linkedTariffHint' : 'hint'}`}>
            <S.Link onClick={() => showLinkedSubjectsModal(subjects)} />
          </Trans>
        </Text>
        <ModalCenterImage as={subjects ? PlanSuccessImage : CalendarBoardImage} />
      </ModalContent>
      <ModalActions>
        <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel')}</ModalCancelButton>
        <ModalSubmitButton onClick={handleSubmit} disabled={isLoading} loading={isLoading}>
          {t('Tariffs:confirmEditModal.submitButton')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

ConfirmModal.displayName = 'ConfirmModal';

const showConfirmModal = (tariff: TAvailableTariff) =>
  showModal((closeModal) => <ConfirmModal closeModal={closeModal} tariff={tariff} />);

type TFormData = TAvailableTariff['data'] & {name: string; connectionFee: number};

const EditTariffModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TAvailableTariff;
}>(({closeModal, tariff}) => {
  const {t} = useTranslation();

  const {data: license} = useGetCatalogTariffLicenseQuery({
    tariffId: tariff.tariffCatalogId,
    licenseId: tariff.licenseDetailId,
  });

  const defaultValues = React.useMemo(() => {
    const baseValues = {
      name: tariff.name,
      license: tariff.data[ELicenseLabelType.LICENSE] ?? DEFAULT_COEFFICIENT,
    };

    if (tariff.channelType === EChannelType.WHATSAPP) {
      return {
        ...baseValues,
        amau: tariff.data[ELicenseLabelType.AMAU] ?? DEFAULT_COEFFICIENT,
        connectionFee: tariff.data[ELicenseLabelType.CONNECTION_FEE] ?? DEFAULT_COEFFICIENT,
      };
    }

    return baseValues;
  }, [tariff]);

  const methods = useForm<TFormData>({
    mode: 'all',
    defaultValues,
    resolver: joiResolver((joi) =>
      tariff.channelType === EChannelType.WHATSAPP
        ? joi.object({
            license: joi.number().required(),
            connectionFee: joi.number().required(),
            amau: joi.number().required(),
          })
        : joi.object({license: joi.number().required()}),
    ),
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  const handleSubmit = React.useCallback(
    (values: TFormData) => {
      showConfirmModal({
        ...tariff,
        data: {
          ...pick(values, 'license', 'amau'),
          [ELicenseLabelType.CONNECTION_FEE]: values.connectionFee,
        },
        activeFrom: combineDateAndTime(
          startOfMonth(addMonths(new Date(), 1)),
          DEFAULT_ACTIVATION_TIME,
        ),
      });
      closeModal();
    },
    [tariff, closeModal],
  );

  return (
    <Modal width="830px">
      <FormProvider {...methods}>
        <ModalHeader>{t('Tariffs:editTariffModal.title', {name: tariff.name})}</ModalHeader>
        <ModalContent>
          <NewTextInputField name="name" readOnly noMargin />
          <Text topIndent="4" bottomIndent="4">
            {t('Tariffs:editTariffModal.coefficients')}
          </Text>
          <CoefficientsForm channelType={tariff.channelType} license={license} />
        </ModalContent>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton onClick={methods.handleSubmit(handleSubmit)}>
            {t('Tariffs:editTariffModal.planButton')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

EditTariffModal.displayName = 'EditTariffModal';

export const showEditTariffModal = (tariff: TAvailableTariff) =>
  showModal((closeModal) => <EditTariffModal closeModal={closeModal} tariff={tariff} />);
